// =============================================================================
// returnTo — a cross-link records where it came from, so the destination's back
// arrow means "back" instead of guessing (BigExport nav audit 2026-08-17, N4).
//
// The defect this closes: a link pushes you from a RECORD to another page, and
// that page's arrow goes to ITS default — usually a list. Shipment detail →
// "Build invoice" → back landed on the invoices list, then the Overview; the
// return trip cost five taps and a search. The mirror link did the same in
// reverse, so a user alternating the two never got back to either record.
//
// The shape is not new: <ExporterLoads> already reads `location.state.returnTo`
// (set by the shipment hub's "Import loads file") and is the only page in the
// module that returns you correctly. This lifts that one-off into the shared
// convention so every cross-link can use it — Loads needs no change.
//
// Router STATE, not a query param, deliberately: a return path is navigation
// bookkeeping, not part of the page's identity. Putting it in the URL would
// make two arrivals at the same record look like different pages to caches and
// deep links, and would leak an internal path into anything a user copies.
//
// ⚠️ Pages that clear consumed router state (`navigate('.', { replace: true,
// state: null })` — Invoices, Shipments) must PRESERVE returnTo when they do,
// or the clear silently deletes the way home. Pass `state: returnTo ? {
// returnTo } : null` there.
// =============================================================================

import { useCallback } from 'react'
import { useLocation, useNavigate, type NavigateOptions } from 'react-router-dom'

/** The router-state key. One place, so a typo can't half-wire a round trip. */
const KEY = 'returnTo'

type ReturnState = { returnTo?: unknown } | null

/**
 * The path stamped on a cross-link by {@link useNavigateWithReturn}, or null
 * when the page was reached directly (deep link, refresh, hub tile).
 *
 * Always pair with a fallback — `backTo={returnTo ?? '/exporter'}` — so a
 * deep-linked page still has a way out.
 */
export function useReturnTo(): string | null {
  const { state } = useLocation()
  const raw = (state as ReturnState)?.[KEY]
  // Router state is attacker-adjacent (it survives history, and anything can
  // push a state object), so only accept an in-app absolute path. A protocol-
  // relative "//evil.example" would otherwise navigate off-origin.
  if (typeof raw !== 'string') return null
  if (!raw.startsWith('/') || raw.startsWith('//')) return null
  return raw
}

/**
 * The state a `<Link>` passes to stamp the same return path as
 * {@link useNavigateWithReturn} — `<Link to="…" state={useReturnHereState()}>`.
 * For declarative cross-links where swapping in a button would lose the
 * anchor semantics.
 */
export function useReturnHereState(): { returnTo: string } {
  const { pathname, search } = useLocation()
  return { [KEY]: `${pathname}${search}` }
}

/**
 * `navigate` that stamps the CURRENT location as the destination's return path.
 *
 * Use for a cross-link between two pages — not for "up" or "home" moves, which
 * should replace rather than push (see <HomeButton/>).
 *
 * `returnPath` overrides the stamped path for a view the URL does not name —
 * a tab held in state (a bare /config opens Config's remembered tab without
 * naming it in the URL). Must be an in-app absolute path.
 */
export function useNavigateWithReturn(returnPath?: string): (to: string, opts?: NavigateOptions) => void {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  // Include the query: Statement is reached as `?receiver=<id>`, and returning
  // to a bare /exporter/statement would drop the customer being looked at.
  const here = returnPath ?? `${pathname}${search}`
  return useCallback((to: string, opts?: NavigateOptions) => {
    navigate(to, {
      ...opts,
      state: { ...(opts?.state as Record<string, unknown> | undefined), [KEY]: here },
    })
  }, [navigate, here])
}
